/**
 * Wave geo · 2026-05-21 — Lazy loader for the Google Maps JS SDK + Places lib.
 *
 * Singleton: a single Promise is memoised the first time `loadGoogleMaps()`
 * is called. Every subsequent call returns the same Promise so we never
 * inject the script twice (the SDK throws "You have included the Google
 * Maps JavaScript API multiple times on this page" if you do).
 *
 * The SDK is ~150KB gzipped. It is NEVER added to `index.html` — callers
 * must request it explicitly, so the home/landing/login pages stay lean.
 *
 * Failure modes:
 *   - `VITE_GOOGLE_MAPS_API_KEY` missing → rejects immediately. Components
 *     that catch this fall back to a plain `<input>`.
 *   - Script tag onerror (network blocked / 403 from key restrictions) →
 *     the rejection bubbles. Subsequent calls return the SAME rejected
 *     Promise (deliberate: avoids hammering Google with retries; user can
 *     reload the page to retry).
 *
 * The callback name is randomised per loader to avoid collisions when
 * multiple Builder instances co-exist (e.g. dev + storybook in the same
 * tab, or HMR after editing this file).
 */

// Minimal Google Maps namespace types. We deliberately don't depend on
// `@types/google.maps` to keep the install lean — the loader only exposes
// the surface the rest of the app calls. Components that need richer typing
// can `as unknown as google.maps.Foo` locally.
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace google {
    namespace maps {
      class Map {
        constructor(el: Element, opts?: Record<string, unknown>);
      }
      class Marker {
        constructor(opts?: Record<string, unknown>);
        setMap(map: Map | null): void;
      }
      class LatLng {
        constructor(lat: number, lng: number);
        lat(): number;
        lng(): number;
      }
      namespace places {
        class Autocomplete {
          constructor(input: HTMLInputElement, opts?: Record<string, unknown>);
          addListener(event: string, handler: () => void): void;
          getPlace(): {
            place_id?: string;
            formatted_address?: string;
            geometry?: { location?: { lat(): number; lng(): number } };
            address_components?: Array<{
              long_name: string;
              short_name: string;
              types: string[];
            }>;
          };
        }
      }
      namespace event {
        function clearInstanceListeners(instance: unknown): void;
      }
    }
  }
}

let loaderPromise: Promise<typeof google.maps> | null = null;

/**
 * Lazy-load the Google Maps JS SDK + Places library on first call.
 * Returns a cached Promise on subsequent calls so the script is injected
 * exactly once per browser tab.
 */
export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (loaderPromise) return loaderPromise;

  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!key) {
    // Eager reject (and DON'T memoise) so a deferred dev-time setup of
    // the env var still works on next call (after page reload).
    return Promise.reject(
      new Error(
        'VITE_GOOGLE_MAPS_API_KEY missing. ' +
          'Set it in apps/web/.env.production (or .env.local for dev) ' +
          'and rebuild — the Maps SDK cannot load without it.',
      ),
    );
  }

  loaderPromise = new Promise<typeof google.maps>((resolve, reject) => {
    // Already loaded in a previous tab navigation that survived (rare with
    // SPA, but defensive). If the SDK self-registered, just resolve.
    if (typeof window !== 'undefined' && (window as { google?: { maps?: typeof google.maps } }).google?.maps) {
      resolve((window as unknown as { google: { maps: typeof google.maps } }).google.maps);
      return;
    }

    const cbName = `__gmaps_cb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    (window as unknown as Record<string, () => void>)[cbName] = () => {
      try {
        const maps = (window as unknown as { google: { maps: typeof google.maps } }).google.maps;
        resolve(maps);
      } catch (e) {
        reject(e);
      } finally {
        delete (window as unknown as Record<string, () => void>)[cbName];
      }
    };

    const script = document.createElement('script');
    script.src =
      `https://maps.googleapis.com/maps/api/js` +
      `?key=${encodeURIComponent(key)}` +
      `&libraries=places` +
      `&callback=${cbName}` +
      `&language=pt-BR&region=BR&loading=async`;
    script.async = true;
    script.defer = true;
    script.onerror = (err) => {
      // Reset so a manual reload can retry; otherwise the rejected Promise
      // would short-circuit forever.
      loaderPromise = null;
      delete (window as unknown as Record<string, () => void>)[cbName];
      reject(
        new Error(
          'Failed to load Google Maps SDK. ' +
            'Check VITE_GOOGLE_MAPS_API_KEY referrer restrictions in Google Cloud Console.',
        ),
      );
      // Touch err so eslint no-unused-vars stays happy if anyone tightens rules.
      void err;
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
}

/**
 * Test-only escape hatch. Resets the cached promise so consecutive test
 * cases can re-mount components without polluting the global SDK state.
 * No-op in production code.
 */
export function __resetGoogleMapsLoaderForTests(): void {
  loaderPromise = null;
}
